import { browserName, browserVersion, osName } from "react-device-detect";

/**
 * Browser, environment and time context for the Report a problem dialog (AKL-798).
 *
 * Pure functions only — nothing here touches React state, web storage or the network, so
 * every branch is directly unit-testable.
 *
 * `ReportContext` is declared here rather than in `ObjectTypes.tsx` (which `CLAUDE.md` names
 * as the home for shared types). A deliberate deviation: it is internal to this feature and
 * never crosses a component boundary that does not already import this module.
 */

/** Stable machine value for the `app_env` payload field. Frozen with the API contract. */
export type AppEnvironment = "local" | "dev" | "qa" | "prod" | "unknown";

/** Translation keys for the four environments we recognise. */
export type EnvironmentLabelKey = "Local" | "Development" | "QA" | "Production";

export type EnvironmentDescription = {
  /** Translation key for a recognised environment; null when the host is not one of ours. */
  labelKey: EnvironmentLabelKey | null;
  /**
   * What to show when `labelKey` is null: the API host, verbatim. An unrecognised host is
   * exactly the case triage wants the literal value for, so no label is invented for it.
   * Empty when the API address is unset or unparseable.
   */
  hostLabel: string;
  /** The value sent to the API. */
  value: AppEnvironment;
};

/** Everything the dialog captures once, when it opens. */
export type ReportContext = {
  /** The path the user was on, query string stripped (see `reportPath.ts`). */
  page: string;
  /** The signed-in user's email, for display only — the server trusts the request token. */
  email: string;
  /** e.g. `Chrome 128 / Windows`. Empty when nothing could be detected. */
  browser: string;
  /** The raw user agent, sent for triage but never shown in the panel. */
  userAgent: string;
  /** The environment, as a translation key or a literal host. */
  environment: EnvironmentDescription;
  /** The API host, for the payload. */
  apiHost: string;
  /** ISO-8601 UTC, for the payload. */
  clientTime: string;
  /** The same instant, formatted for the panel's `Time (UTC)` row. */
  clientTimeDisplay: string;
};

/** The placeholder `react-device-detect` reports for a segment it could not detect. */
const DEVICE_DETECT_UNKNOWN = "none";

const known = (value: string | undefined): string =>
  !value || value === DEVICE_DETECT_UNKNOWN ? "" : value;

/**
 * A short, readable browser/OS string such as `Chrome 128 / Windows`.
 *
 * Uses `react-device-detect`, which is **already a declared dependency** (and, before this
 * feature, unused anywhere in `src/`) — chosen for exactly that reason rather than
 * hand-rolling `navigator.userAgent` parsing next to an installed user-agent parser.
 *
 * Any segment the library reports as its `"none"` placeholder is dropped rather than
 * rendered, so a partially-detected agent degrades to whatever is actually known.
 * @returns the browser string, or "" when nothing could be detected
 */
export function describeBrowser(): string {
  const name = known(browserName);
  const version = known(browserVersion);
  const os = known(osName);
  const browser = [name, version].filter(Boolean).join(" ");
  return [browser, os].filter(Boolean).join(" / ");
}

/**
 * The host of the configured API address, for the payload.
 *
 * Parsed with `URL` inside a try/catch: `BASE_URL` is `process.env.REACT_APP_SERVER_URL`
 * with no default, so a malformed or absent value must yield "" rather than throw during
 * render.
 * @param apiBaseUrl the configured API address, possibly undefined
 * @returns the host, or "" when it could not be read
 */
export function apiHostOf(apiBaseUrl: string | undefined): string {
  if (!apiBaseUrl) return "";
  try {
    return new URL(apiBaseUrl).host;
  } catch {
    return "";
  }
}

/** Hostnames that mean "a developer's machine", whichever loopback form is configured. */
const LOOPBACK_HOSTNAMES = [
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "[::1]",
  "::1",
];

/**
 * Which environment the user is on, derived from the configured API address.
 *
 * Two traps this handles on purpose. `BASE_URL` types as `string | undefined` (`base.tsx`
 * exports `process.env.REACT_APP_SERVER_URL` with no default), so `undefined` and "" must
 * return an unset result instead of interpolating `"undefined"`. And local detection cannot
 * match only `localhost`: `.local.env` sets `http://localhost:8000` but `.example.env` — the
 * file a new developer copies — sets `http://127.0.0.1:8000`, so matching `localhost` alone
 * would misclassify the documented local setup.
 *
 * The deployed hosts are matched as substrings of the whole address, so a path or a trailing
 * slash cannot defeat the check.
 * @param apiBaseUrl the configured API address, possibly undefined
 * @returns the label key (or the literal host) plus the stable machine value
 */
export function deriveEnvironment(
  apiBaseUrl: string | undefined,
): EnvironmentDescription {
  if (!apiBaseUrl) {
    return { labelKey: null, hostLabel: "", value: "unknown" };
  }
  const host = apiHostOf(apiBaseUrl);
  const hostname = host.replace(/:\d+$/, "");
  if (LOOPBACK_HOSTNAMES.includes(hostname)) {
    return { labelKey: "Local", hostLabel: host, value: "local" };
  }
  if (apiBaseUrl.includes("akili-api-dev-we")) {
    return { labelKey: "Development", hostLabel: host, value: "dev" };
  }
  if (apiBaseUrl.includes("akili-api-qa-we")) {
    return { labelKey: "QA", hostLabel: host, value: "qa" };
  }
  if (apiBaseUrl.includes("akili-api-prod-we")) {
    return { labelKey: "Production", hostLabel: host, value: "prod" };
  }
  return { labelKey: null, hostLabel: host, value: "unknown" };
}

/**
 * The current instant as ISO-8601 UTC, for the `client_time` payload field.
 * @returns e.g. `2026-09-11T14:32:05.123Z`
 */
export function nowUtcIso(): string {
  return new Date().toISOString();
}

/**
 * The panel's `Time (UTC)` value: minute precision, explicitly UTC.
 *
 * `formatDateAsLocalString` in `helpers.tsx` is deliberately not reused — it emits a local
 * `yyyy-mm-dd`, and this row is labelled UTC and needs the time of day.
 * @param iso an ISO-8601 timestamp, as produced by `nowUtcIso`
 * @returns e.g. `2026-09-11 14:32 UTC`, or "" when the input is not a readable date
 */
export function formatUtcTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}
